import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const TenantContext = createContext(null);

export function TenantProvider({ children }) {
  const { user } = useAuth();
  const [tenant, setTenant] = useState(null);          // current active tenant
  const [tenants, setTenants] = useState([]);          // all visible tenants
  const [loading, setLoading] = useState(false);
  const [impersonating, setImpersonating] = useState(false);

  const isSuperSuperAdmin = user && user.role === "super_super_admin";

  const fetchTenants = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [meR, listR] = await Promise.all([
        api.get("/tenants/me"),
        api.get("/tenants"),
      ]);
      setTenant(meR.data);
      setTenants(listR.data || []);
    } catch (e) {
      // ignore — auth interceptor handles 401
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) fetchTenants();
    else { setTenant(null); setTenants([]); }
  }, [user, fetchTenants]);

  // Active navy `--primary` theme is injected per-surface by
  // Login/AdminLayout/Layout from branding.admin_button_color.

  /** Super-super-admin: switch to another tenant via impersonation. */
  async function switchTenant(targetTenantId) {
    if (!isSuperSuperAdmin) return;
    if (!targetTenantId || targetTenantId === tenant?.id) return;
    const { data } = await api.post(`/tenants/${targetTenantId}/impersonate`);
    if (data?.token) {
      // Store original token once, swap to impersonation token
      const orig = localStorage.getItem("c7_token");
      if (!localStorage.getItem("c7_token_orig") && orig) {
        localStorage.setItem("c7_token_orig", orig);
      }
      localStorage.setItem("c7_token", data.token);
      setImpersonating(true);
      // Hard reload so all hooks re-fetch under the new scope
      window.location.reload();
    }
  }

  /** Stop impersonation, return to operator tenant. */
  function stopImpersonation() {
    const orig = localStorage.getItem("c7_token_orig");
    if (orig) {
      localStorage.setItem("c7_token", orig);
      localStorage.removeItem("c7_token_orig");
      setImpersonating(false);
      window.location.reload();
    }
  }

  // Detect impersonation state on first load (token swapped externally)
  useEffect(() => {
    if (localStorage.getItem("c7_token_orig")) setImpersonating(true);
  }, []);

  return (
    <TenantContext.Provider value={{
      tenant, tenants, loading,
      isSuperSuperAdmin,
      impersonating,
      switchTenant, stopImpersonation,
      refresh: fetchTenants,
    }}>
      {children}
    </TenantContext.Provider>
  );
}

export const useTenant = () => useContext(TenantContext) || {};
